import Request from '../../../../src/core/gd/data/request'
import {ProjectAccess} from "../const/project";

export default class ApiBulkActions {

    constructor() {
    }

    moveTasksToProject(taskIds: ID[], toProjectId: ID) {
        return new Request(this, 'PUT', 'bulk/tasks/move-to-project/'+toProjectId, {
            tasks: taskIds
        });
    }

    copyTasksToProject(taskIds: ID[], toProjectId: ID, data: any) {
        data['taskIds'] = taskIds;
        return new Request(this, 'POST', 'bulk/tasks/copy-to-project/'+toProjectId, data);
    }

    moveProjectsToProject(projectIds: ID[], toProjectId: ID) {
        return new Request(this, 'POST', 'bulk/projects/move', {
            projectIds      : projectIds,
            parentProjectId : toProjectId
        });
    }

    addTag(taskIds: ID[], tagId: ID) {
        return new Request(this,'POST','bulk/tasks/tag/'+tagId,{
            tasks: taskIds
        });
    }

    removeTag(taskIds: ID[], tagId: ID) {
        return new Request(this,'POST','bulk/tasks/untag/'+tagId,{
            tasks: taskIds
        });
    }

    removeAllTags(taskIds: ID[]) {
        return new Request(this,'POST','bulk/tasks/untag-all',{
            tasks: taskIds
        });
    }

    // Bulk add many users to many tasks in a single request. `users` is
    // one row per user, each carrying the list of workspaces / sub-
    // projects that user is still missing from. workspaceAccess applies
    // to every id in workspaceProjectIds; projectAccess applies to every
    // id in projectIds. Backend regroups by projectId so each project's
    // project_user rows are written via one SQL, and fires exactly one
    // onTaskUsersChanged per touched task with the fully-updated user
    // list (no last-write-wins races between per-user requests).
    static addUsersToTasks(
        taskIds : ID[],
        users   : Array<{
            userId               : ID
            workspaceProjectIds? : ID[]
            workspaceAccess?     : ProjectAccess
            projectIds?          : ID[]
            projectAccess?       : ProjectAccess
        }>,
    ) {
        return new Request(this, 'POST', 'bulk/tasks/users/add', {
            taskIds,
            users,
        });
    }

    // Bulk remove many users from many tasks in a single request. Backend
    // skips (task, user) pairs where the user is the assignee /
    // action-required / not a member, and fires one onTaskUsersChanged per
    // touched task with the fully-reduced user list.
    static removeUsersFromTasks(taskIds: ID[], userIds: ID[]) {
        return new Request(this, 'POST', 'bulk/tasks/users/remove', {
            taskIds,
            userIds,
        });
    }

    // Per-user "Notify me about" save - works for one task or many. The body sends the full
    // notification config as a nested object so the backend can persist it verbatim in
    // task_user.notifications_settings.
    updateTasksNotifications(taskIds: ID[], config: { mode: string, email?: any }) {
        return new Request(this,'PUT','bulk/tasks/notifications',{
            taskIds,
            notifications: {
                mode    : config.mode,
                email   : config.email ?? null,
            },
        });
    }

    /*changeTaskType(taskIds, taskTypeId) {
        return new Request(this,'POST','tasks/task-type',{
            taskIds: taskIds,
            taskTypeId: taskTypeId
        });
    }*/

    projectsConvert(projectIds: ID[], templateId: ID) {
        return new Request(this,'POST','bulk/projects/convert',{
            projectIds  : projectIds,
            templateId  : templateId
        });
    }

    projectsConvertV2(params: any) {
        return new Request(this,'POST','bulk/projects/convert-v2',params);
    }

    deleteTasks(taskIds: ID[]) {
        return new Request(this,'POST','bulk/tasks/delete',{
            taskIds: taskIds
        });
    }

    updateTasks(taskIds: ID[], params: any) {

        // example params
        /*const requestParams = {
            taskIds             : ["3yyZQM","1gj4JW","fzjzUP"],
            priority            : 8,
            statusId            : "8hA8DU",
            actionRequiredUserId: "KElaaH",
            assignedToUserId    : "AczQKr",
            taskTypeId          : "snX3oP",
            scheduleDate        : "2022-03-25",
            storyPoints         : null,
            estimate            : 60,
            deadline            : "2022-03-30",
            startDate           : "2022-04-05",
            endDate             : "2022-04-07",
            progress            : 50,
        }*/

        const requestParams = _.assign({taskIds}, params)

        // Update scheduleDate dependency check
        if (_.has(requestParams, 'statusId') ) {
            gd.modules.validations.updateRequestPayloadOnTaskStatusChange(requestParams)
        }
        // Update scheduleDate dependency check
        if (_.has(requestParams, 'scheduleDate') ) {
            gd.modules.validations.updateRequestPayloadOnScheduleChange(requestParams)
        }



        return new Request(this,'POST','bulk/tasks/update',requestParams);
    }

    updateTasksV2(tasksUpdatesMap: any) {

        // example params
        /*const tasksUpdatesMap = {
            [task_id_1]: {crmContactIds: [ID1, ID2]},
            [task_id_2]: {crmContactIds: [ID1]},
        }*/

        return new Request(this,'POST','bulk/tasks/update-v2',tasksUpdatesMap);
    }

    updateTasksTimeAllocation(tasks: any) {
        return new Request(this,'POST','bulk/tasks/time-allocation', {
            tasks
        });
    }

    updateTasksStatus(statusUpdates: any) {
        // example params
        /*const statusUpdates = {
            status1Id: [id, id, id],
            status2Id: [id,id,id]
        }*/

        const payload = {statusUpdates}

        // Update status dependency check
        gd.modules.validations.updateRequestPayloadOnTaskStatusChange(payload)

        return new Request(this,'PUT','bulk/tasks/status', payload);
    }

    updateTasksPlanning(data: any) {
        // example data
        /*const data = [
            {
                id: taskId,
                startDate,
                endDate,
                scheduleDate, // - optional -don't pass this key if no update
            }
        ]*/
        let payload = {data}
        gd.modules.validations.updateRequestPayloadOnTaskStartEndDateChange(payload)
        return new Request(this,'PUT','bulk/tasks/planning', payload);
    }

    updateTasksCustomField(tasks: any[], customFieldId: ID, newValue: any) {
        return new Request(this,'PUT','bulk/tasks/custom-field', {
            tasks,
            customFieldId,
            newValue,
        });
    }


    updateReminders(reminders: any[]) {

        // parser.add_argument('reminders', type=dict, action='append', required=True)
        // each reminder is object with following possible params
        // parser.add_argument('isArchived',type=inputs.boolean, required=False)
        // parser.add_argument('scheduleStatus',type=int, required=False)
        // parser.add_argument('scheduleDate',type=inputDate, required=False)
        // parser.add_argument('scheduleMoment',type=inputMoment, required=False)

        return new Request(this,'POST','bulk/reminders/update-new', {reminders})
    }

    deleteReminders(reminderIds: ID[]) {
        return new Request(this,'POST','bulk/reminders/delete',{reminderIds})
    }

    updateMeetings(meetingsIds: ID[], params: any) {
        const requestParams = _.assign({meetingsIds}, params)
        return new Request(this,'POST','bulk/meetings/update',requestParams)
    }

    updateEvents(eventIds: ID[], params: any) {

        // parser.add_argument('eventIds', type=inputEncodedId, action='append', required=True)
        // parser.add_argument('assignedToUserId', type=inputEncodedId, required=False)
        // parser.add_argument('isAccomplished', type=boolean, required=False)
        // parser.add_argument('startDate', type=inputDate, required=False)
        // parser.add_argument('endDate', type=inputDate, required=False)

        const requestParams = _.assign({eventIds}, params)
        return new Request(this,'POST','bulk/events/update',requestParams)
    }

    static updateMyWorks(todosIds: ID[], params: {
        scheduleDate?       : string | null
        markAsCompleted?    : boolean
    }) {

        const requestParams = {
            ids             : todosIds,
            scheduleDate    : params.scheduleDate,
            markAsCompleted : params.markAsCompleted
        }

        return new Request(this,'PUT','bulk/my-works/update', requestParams)
    }

    static deleteMyWorks(todosIds: ID[]) {
        return new Request(this,'POST','bulk/my-works/delete',{ids: todosIds})
    }
}

